/** @typedef {import('../index.js').BaseDocData} BaseDocData */
import { Timestamp } from 'firebase/firestore';
import * as Yup from 'yup';

export const projectSchema = Yup.object().shape({
  name: Yup.string()
    .required('Project name is required')
    .when('isInternal', {
      is: true,
      then: Yup.string()
        .length(4, 'Internal project ID must be exactly 4 characters long')
        .matches(/^P\d{3}$/, 'Internal project ID must start with "P" and be followed by 3 digits'),
      otherwise: Yup.string()
        .length(6, 'Customer project ID must be exactly 6 characters long')
        .matches(/^C\d{5}$/, 'Customer project ID must start with "C" and be followed by 5 digits'),
    }),
  isInternal: Yup.boolean().default(false).required('isInternal is required'),
  uid: Yup.string(),
  notes: Yup.string().default(''),
});

/**
 * @typedef {Yup.InferType<typeof projectSchema>} ProjectSchema
 * @typedef {BaseDocData & ProjectSchema & { createdAt: Timestamp; }} ProjectDocData
*/

/**
 * Class representing a project.
 * @class
 */
export default class Project {
  /** @type {ProjectDocData['validation']} */ validation = 'passed';
  /** Firestore document ID @type {ProjectDocData['id']} */ id;
  /** @type {ProjectDocData['name']} */ name;
  /** @type {ProjectDocData['isInternal']} */ isInternal;
  /** @type {ProjectDocData['notes']} */ notes = '';
  /** @type {ProjectDocData['uid']} */ uid;
  /** @type {ProjectDocData['createdAt']} */ createdAt;

  /**
   * Create a project.
   * @param {any} param0 - Raw Firestore document.
   * @throws {Yup.ValidationError} - Raw Firestore document failed Yup validation.
   */
  constructor({
    id, createdAt, uid, name, isInternal, notes = '',
  }) {
    // Validate required fields
    const validatedData = projectSchema.validateSync({
      name,
      isInternal,
      notes,
      uid,
    });

    this.id = id || null;
    this.name = validatedData.name;
    this.isInternal = validatedData.isInternal;
    this.notes = validatedData.notes;
    this.uid = validatedData.uid;
    this.createdAt = createdAt;
  }

  /**
   * Converts the {@link Project} instance to Firestore document data.
   * @returns {Omit<ProjectDocData, 'id'>} {@link ProjectDocData} (without `id`).
   */
  toFirestore({ serverTimestamp }) {
    return {
      name: this.name,
      isInternal: this.isInternal,
      notes: this.notes,
      createdAt: serverTimestamp(),
      uid: this.uid,
    };
  }
}
